import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle, CheckCircle2, ImageIcon, Download } from "lucide-react";
import { apiFetch, resolveApiUrl } from "@/lib/jwt-auth-client";

// ── Types (mirror core/src/domains/managed-agent/jobTracker.ts) ───────────

type JobStatus = "submitted" | "running" | "completed" | "failed";

interface JobStatusResponse {
  jobId: string;
  sessionId: string;
  status: JobStatus;
  taskType: string;
  result?: unknown;
  error?: string;
  metadata?: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

interface RuntimeImageStatusResponse {
  status: string;
  totalImages?: number;
  completedImages?: number;
}

export interface BananJobResult {
  agentResponse?: string;
  imageUrl?: string;
  imageUrls?: string[];
}

/** Subset of BananBatchItemLineage fields used to label a tile. Mirrors the
 *  lineage written by dispatchBananBatch; keep loose so it survives added
 *  lineage fields without a type break. */
interface TileLineage {
  sourceResearchAngle?: string;
  targetProblem?: string;
  overlayText?: string;
  avatarName?: string;
  sourceHookType?: string;
  ugcSceneSlot?: string;
  sourceVideoScriptAngle?: string;
}

/** Pick a two-line caption for a tile from lineage metadata. Returns either
 *  field empty — the component hides what isn't set. Order matches Banan use
 *  cases: scene-slot storyboard, avatar portrait, research-angle ad, then
 *  plain overlay fallback. */
function tileCaption(
  lineage: TileLineage | undefined,
  fallbackHeading: string,
): { heading: string; subheading?: string } {
  if (!lineage) return { heading: fallbackHeading };
  if (lineage.ugcSceneSlot) {
    return {
      heading: lineage.ugcSceneSlot.toUpperCase(),
      subheading: lineage.sourceVideoScriptAngle,
    };
  }
  if (lineage.avatarName) {
    return {
      heading: lineage.avatarName,
      subheading: lineage.sourceHookType ?? lineage.targetProblem,
    };
  }
  if (lineage.sourceResearchAngle || lineage.targetProblem) {
    return {
      heading: lineage.sourceResearchAngle ?? lineage.targetProblem ?? fallbackHeading,
      subheading: lineage.overlayText,
    };
  }
  if (lineage.overlayText) {
    return { heading: fallbackHeading, subheading: lineage.overlayText };
  }
  return { heading: fallbackHeading };
}

function extractBatchItems(metadata: Record<string, unknown> | undefined): TileLineage[] {
  const raw = metadata?.batchItems;
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is TileLineage => !!v && typeof v === "object");
}

interface BananJobCardProps {
  jobId: string;
  /** Number of images being generated (for batch display). */
  itemCount?: number;
  onComplete?: (jobId: string, result: BananJobResult) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5_000;
const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set(["completed", "failed"]);
const RUNTIME_SESSION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ── Helpers ───────────────────────────────────────────────────────────────

function isTerminal(status: JobStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

function normalizeRuntimeImageStatus(data: RuntimeImageStatusResponse): JobStatus {
  const total = typeof data.totalImages === "number" ? data.totalImages : 0;
  const completed = typeof data.completedImages === "number" ? data.completedImages : 0;
  if (
    data.status === "completed" ||
    data.status === "complete" ||
    (total > 0 && completed >= total)
  ) {
    return "completed";
  }
  if (
    data.status === "failed" ||
    data.status === "aborted" ||
    data.status === "cancelled"
  ) {
    return "failed";
  }
  return data.status === "submitted" ? "submitted" : "running";
}

function extractBananResult(result: unknown): BananJobResult {
  if (!result || typeof result !== "object") return {};
  const rec = result as Record<string, unknown>;
  const parsed: BananJobResult = {};

  if (typeof rec.agentResponse === "string" && rec.agentResponse.trim()) {
    parsed.agentResponse = rec.agentResponse.trim();
  }
  if (typeof rec.imageUrl === "string" && rec.imageUrl.trim()) {
    parsed.imageUrl = rec.imageUrl.trim();
  }
  if (Array.isArray(rec.imageUrls)) {
    const urls = rec.imageUrls.filter(
      (u): u is string => typeof u === "string" && u.trim().length > 0,
    );
    if (urls.length > 0) parsed.imageUrls = urls;
  }

  // Use first imageUrls entry as imageUrl fallback
  if (!parsed.imageUrl && parsed.imageUrls?.length) {
    parsed.imageUrl = parsed.imageUrls[0];
  }

  return parsed;
}

function formatElapsed(startMs: number): string {
  const seconds = Math.floor((Date.now() - startMs) / 1_000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

// ── Component ─────────────────────────────────────────────────────────────

export function BananJobCard({ jobId, itemCount, onComplete }: BananJobCardProps) {
  const [job, setJob] = useState<JobStatusResponse | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState("");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const startTimeRef = useRef(Date.now());
  const completeFiredRef = useRef(false);
  const isRuntimeImageSession = RUNTIME_SESSION_ID_RE.test(jobId);

  // Resolve itemCount from prop or job metadata
  const resolvedItemCount =
    itemCount ??
    (job?.metadata?.itemCount as number | undefined) ??
    undefined;
  const isBatch = (resolvedItemCount ?? 1) > 1;

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch(
        resolveApiUrl(
          isRuntimeImageSession
            ? `/core/agent/runtime-sessions/${encodeURIComponent(jobId)}/status`
            : `/core/agent/managed-chat/status/${encodeURIComponent(jobId)}`,
        ),
      );
      if (!mountedRef.current) return;
      if (!res.ok) {
        if (res.status === 404) {
          setPollError("Job not found");
        }
        return;
      }
      const raw = await res.json();
      const data: JobStatusResponse = isRuntimeImageSession
        ? {
            jobId,
            sessionId: jobId,
            status: normalizeRuntimeImageStatus(raw as RuntimeImageStatusResponse),
            taskType: "image_generation",
            createdAt: startTimeRef.current,
            updatedAt: Date.now(),
          }
        : (raw as JobStatusResponse);
      setJob(data);
      setPollError(null);
      if (isTerminal(data.status)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    } catch {
      if (!mountedRef.current) return;
      setPollError("Failed to check job status");
    }
  }, [isRuntimeImageSession, jobId]);

  // Start polling on mount
  useEffect(() => {
    mountedRef.current = true;
    startTimeRef.current = Date.now();

    // Initial fetch
    void fetchStatus();

    // Poll every 5s
    intervalRef.current = setInterval(() => {
      void fetchStatus();
    }, POLL_INTERVAL_MS);

    // Elapsed timer (every second while running)
    timerRef.current = setInterval(() => {
      setElapsed(formatElapsed(startTimeRef.current));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchStatus]);

  // Fire onComplete once when job reaches completed status
  useEffect(() => {
    if (!job || job.status !== "completed" || completeFiredRef.current) return;
    completeFiredRef.current = true;
    if (isRuntimeImageSession) return;
    const parsed = extractBananResult(job.result);
    onComplete?.(jobId, parsed);
  }, [isRuntimeImageSession, job, jobId, onComplete]);

  const status: JobStatus = job?.status ?? "submitted";

  // ── Failed ──
  if (status === "failed") {
    const errorMsg = job?.error ?? pollError ?? "Image generation failed";
    return (
      <div className="my-2 rounded-2xl bg-surface-low overflow-hidden text-sm border border-red-500/30">
        <div className="px-4 py-3 flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-red-600 dark:text-red-300">
              {isBatch
                ? `Batch image generation failed (${resolvedItemCount} images)`
                : "Image generation failed"}
            </p>
            <p className="text-[11px] text-red-500/80 dark:text-red-400/70 mt-0.5 break-words">
              {errorMsg}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Completed ──
  if (status === "completed") {
    const parsed = extractBananResult(job?.result);
    const allUrls = parsed.imageUrls ?? (parsed.imageUrl ? [parsed.imageUrl] : []);
    const lineage = extractBatchItems(job?.metadata);

    return (
      <div className="my-2 rounded-2xl bg-surface-low overflow-hidden text-sm border border-indigo-500/30">
        <div className="px-4 py-3 flex flex-col gap-2.5">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
            <p className="text-xs font-medium text-indigo-600 dark:text-indigo-300">
              {allUrls.length > 1
                ? `${allUrls.length} images generated`
                : "Image generation complete"}
            </p>
          </div>

          {/* Multi-image grid with per-tile captions */}
          {allUrls.length > 1 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {allUrls.map((url, i) => {
                const caption = tileCaption(lineage[i], `Image ${i + 1}`);
                return (
                  <figure key={i} className="flex flex-col gap-1 min-w-0 group">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block relative bg-black/5 dark:bg-white/5 rounded-md overflow-hidden"
                    >
                      <img
                        src={url}
                        alt={caption.heading}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                      />
                      <span className="absolute bottom-1 right-1 p-1 rounded-md bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <Download className="w-3 h-3" />
                      </span>
                    </a>
                    <figcaption className="min-w-0">
                      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-foreground/75 truncate">
                        {caption.heading}
                      </div>
                      {caption.subheading && (
                        <div className="text-[11px] text-foreground/60 leading-snug line-clamp-2 break-words">
                          {caption.subheading.startsWith('"') || caption.subheading.startsWith("'")
                            ? caption.subheading
                            : `"${caption.subheading}"`}
                        </div>
                      )}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          )}

          {/* Single image */}
          {allUrls.length === 1 && (
            <figure className="flex flex-col gap-1.5">
              <a
                href={allUrls[0]}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg overflow-hidden bg-black/5 dark:bg-white/5"
              >
                <img
                  src={allUrls[0]}
                  alt={tileCaption(lineage[0], "Generated image").heading}
                  className="w-full max-h-[320px] object-contain"
                />
              </a>
              {(() => {
                const caption = tileCaption(lineage[0], "Generated image");
                return (
                  <figcaption className="min-w-0">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wide text-foreground/75">
                      {caption.heading}
                    </div>
                    {caption.subheading && (
                      <div className="text-[11px] text-foreground/60 leading-snug break-words">
                        {caption.subheading.startsWith('"') || caption.subheading.startsWith("'")
                          ? caption.subheading
                          : `"${caption.subheading}"`}
                      </div>
                    )}
                  </figcaption>
                );
              })()}
            </figure>
          )}

          {allUrls.length === 0 && parsed.agentResponse && (
            <p className="text-[11px] text-foreground/70 whitespace-pre-wrap break-words leading-relaxed pl-6.5">
              {parsed.agentResponse}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Submitted / Running (in progress) ──
  return (
    <div className="my-2 rounded-2xl bg-surface-low overflow-hidden text-sm border border-border/40">
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="relative shrink-0">
          <ImageIcon className="w-4 h-4 text-primary/60" />
          <Loader2 className="w-3 h-3 animate-spin text-primary absolute -top-0.5 -right-1" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-foreground/80">
            {status === "running"
              ? isBatch
                ? `Generating ${resolvedItemCount} images...`
                : "Generating image..."
              : isBatch
                ? `${resolvedItemCount} images queued...`
                : "Image generation queued..."}
          </p>
          <p className="text-[10px] text-foreground/50 mt-0.5">
            {elapsed && `${elapsed} elapsed · `}Job {jobId}
          </p>
        </div>
      </div>
    </div>
  );
}
